import { useEffect, useState } from 'react';

// Review-mode token plumbing. When a user (typically the developer or a
// content reviewer) lands on any page with ?review=<token> in the URL, we
// stash the token in localStorage and forward it to the API as the
// X-Review-Token header on every subsequent request. This gives us a
// cookie-free path that works inside the Replit preview iframe (where
// third-party cookie restrictions can drop the lo_review cookie set by the
// /api/review/enable redirect). Cleared by visiting any page with
// ?review=off (or programmatically via clearReviewToken).
const REVIEW_LS_KEY = 'lo_review_token';
// Custom event broadcast on every token *change* (set, change, or clear) so
// `useReviewMode()` can drop its cached server answer and re-fetch in the
// same SPA session. Without this, a learner who first lands on the site
// without a token (cached `reviewMode=false`) and later appends
// `?review=<token>` to the URL would see no review-mode affordances appear
// because the hook only fetched once on first mount.
const REVIEW_TOKEN_CHANGED = 'lo:review_token_changed';

function notifyReviewTokenChanged() {
  if (typeof window === 'undefined') return;
  try { window.dispatchEvent(new Event(REVIEW_TOKEN_CHANGED)); } catch (_) {}
}

export function captureReviewTokenFromURL() {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const tok = params.get('review');
    if (tok == null) return; // nothing to do
    let changed = false;
    let prev = null;
    try { prev = window.localStorage.getItem(REVIEW_LS_KEY); } catch (_) {}
    if (tok === 'off') {
      if (prev != null) {
        window.localStorage.removeItem(REVIEW_LS_KEY);
        changed = true;
      }
    } else if (tok.trim()) {
      const next = tok.trim();
      if (prev !== next) {
        window.localStorage.setItem(REVIEW_LS_KEY, next);
        changed = true;
      }
    }
    // Scrub ?review=… from the visible URL (and from browser history) so it
    // can't leak into screenshots, copy-pasted links, server access logs on
    // subsequent navigations, or referer headers. localStorage now holds the
    // token; the URL doesn't need to.
    params.delete('review');
    const qs = params.toString();
    const cleanUrl =
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
    window.history.replaceState(window.history.state, '', cleanUrl);
    if (changed) notifyReviewTokenChanged();
  } catch (_) { /* ignore — incognito with localStorage disabled, etc. */ }
}

function getReviewToken() {
  if (typeof window === 'undefined') return null;
  // Always check the URL first — on the very first page load (e.g. the user
  // pastes /courses/.../final-quiz?review=<token>), the courseFinalQuiz
  // fetch effect can fire before ScrollToTop's effect has had a chance to
  // stash the token in localStorage. Reading the URL synchronously here
  // sidesteps that race entirely.
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('review');
    if (fromUrl && fromUrl !== 'off' && fromUrl.trim()) return fromUrl.trim();
  } catch (_) { /* ignore */ }
  try {
    return window.localStorage.getItem(REVIEW_LS_KEY) || null;
  } catch (_) { return null; }
}

function authHeaders(extra) {
  const h = { ...(extra || {}) };
  const tok = getReviewToken();
  if (tok) h['X-Review-Token'] = tok;
  // Dev-mode: the Replit proxy strips Set-Cookie, so the session travels as
  // a Bearer token stored in localStorage. Include it on every API call so
  // the server's sessionMiddleware can resolve req.user in dev.
  try {
    const sid = typeof window !== 'undefined'
      ? window.localStorage.getItem('_loa_dev_sid')
      : null;
    if (sid) h['Authorization'] = `Bearer ${sid}`;
  } catch (_) {}
  return h;
}

// Server-validated review mode. Hits /api/review/status (which checks the
// header / query / cookie token against the server's REVIEW_TOKEN with
// strict equality) and caches the boolean per page load. Use this — NOT
// raw token presence — to gate any UI affordance that should only exist
// for true reviewers (e.g. the Skip-in-review-mode link on the locked
// Final Assessment card). Token presence alone is forgeable client-side
// (anyone can append ?review=foo); only the server can confirm validity.
//
// The hook returns `null` until the first response lands, then `true` or
// `false`. Callers should treat `null` as "not yet known" and render
// nothing rather than flashing the affordance and yanking it away.
// Epoch-stamped cache. Each token-change increments `_reviewModeEpoch`; an
// in-flight `/api/review/status` response only writes the cache if the epoch
// at launch matches the current epoch, so a slow stale request can't
// overwrite a newer correct answer (A→B race) and rapid A→B→C cycles
// always settle on the last-launched response.
let _reviewModeEpoch = 0;
let _reviewModeCache = null;   // { epoch, value } | null
let _reviewModePromise = null; // de-dupes concurrent fetches across hooks
// Module-level pub-sub. The single `lo:review_token_changed` listener below
// invalidates the cache exactly once per token change (no per-hook stomping
// of `_reviewModePromise`) and notifies all subscribed hooks via `_notify`.
const _subscribers = new Set();
function _notify() { _subscribers.forEach((fn) => fn()); }

function _currentCachedValue() {
  // The cache is only "live" if it was written for the current epoch. After
  // an invalidation, hooks treat it as null until the next fetch settles.
  return _reviewModeCache && _reviewModeCache.epoch === _reviewModeEpoch
    ? _reviewModeCache.value
    : null;
}

function fetchReviewMode() {
  if (_reviewModePromise) return _reviewModePromise;
  const launchEpoch = _reviewModeEpoch;
  _reviewModePromise = fetch('/api/review/status', {
    credentials: 'include',
    headers: authHeaders(),
  })
    .then((r) => (r.ok ? r.json() : { reviewMode: false }))
    .then((j) => ({ value: !!(j && j.reviewMode) }))
    .catch(() => ({ value: false }))
    .then(({ value }) => {
      _reviewModePromise = null;
      // Stale-guard: drop the result on the floor if the epoch advanced
      // (i.e. the token changed while we were waiting). The newer fetch
      // already in flight (or about to be kicked off by the listener) is
      // the one that owns the post-change answer.
      if (launchEpoch === _reviewModeEpoch) {
        _reviewModeCache = { epoch: launchEpoch, value };
        _notify();
      }
    });
  return _reviewModePromise;
}

if (typeof window !== 'undefined') {
  // Single, global listener. Without this, every mounted `useReviewMode()`
  // hook would attach its own handler and each one would null
  // `_reviewModePromise` + spawn a separate fetch on the same event —
  // nondeterministic last-write between concurrent fetches. Centralizing
  // the invalidation here means one fetch per token-change, irrespective
  // of how many components are subscribed.
  window.addEventListener('lo:review_token_changed', () => {
    _reviewModeEpoch += 1;
    _reviewModeCache = null;
    _reviewModePromise = null; // abandon any in-flight; stale-guard will discard its result
    _notify();                 // hide review-only affordances during the recheck
    fetchReviewMode();         // kick off the post-change recheck
  });
}

export function useReviewMode() {
  // We don't store the value in component state — the source of truth is
  // the module-level cache, and we re-render on `_notify()`.
  const [, force] = useState(0);
  useEffect(() => {
    const onChange = () => force((n) => n + 1);
    _subscribers.add(onChange);
    // First mount kicks off the initial fetch if nothing has primed the
    // cache yet for the current epoch. Subsequent mounts within the same
    // epoch just read the cached value.
    if (_currentCachedValue() === null) fetchReviewMode();
    return () => { _subscribers.delete(onChange); };
  }, []);
  return _currentCachedValue();
}

export async function fetchJSON(path, options = {}) {
  const { method, headers: extraHeaders, body, ...rest } = options;
  const res = await fetch(path, {
    method: method || 'GET',
    credentials: 'include',
    headers: authHeaders(extraHeaders || {}),
    ...(body !== undefined ? { body } : {}),
    ...rest,
  });
  if (!res.ok) {
    let payload = {};
    try { payload = await res.json(); } catch (_) {}
    throw new Error(payload.error || `Request failed: ${res.status}`);
  }
  return res.json();
}

// All authenticated POSTs MUST go through this helper so the X-Review-Token
// header is attached uniformly. Bypassing it (e.g. raw fetch with just
// credentials:'include') will silently 403 in iframe contexts where the
// lo_review cookie is dropped by the browser as third-party.
export async function postJSON(path, body) {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body || {}),
  });
  let payload = {};
  try { payload = await res.json(); } catch (_) {}
  if (!res.ok) throw new Error(payload.error || `Request failed: ${res.status}`);
  return payload;
}

export function formatPrice(cents, currency = 'USD') {
  const amount = (cents || 0) / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch (_) {
    return `$${amount.toFixed(2)}`;
  }
}

export function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const rest = mins % 60;
  return rest === 0 ? `${hrs} hr` : `${hrs} hr ${rest} min`;
}
